import type { Permission, Role } from '@new-wealth/shared';

/**
 * Navigation catalog (docs/05 §2). Each item lists the permission(s) that
 * make it visible; the sidebar filters by the user's permissions so UI and
 * API never disagree. `hideForRoles` additionally hides an item from specific
 * roles that hold the permission but shouldn't see the section (e.g. CXO is
 * read-only and doesn't work the funnel).
 */
export interface NavItem {
  to: string;
  label: string;
  anyOf: Permission[];
  group: string;
  hideForRoles?: Role[];
}

export const NAV: NavItem[] = [
  { to: '/app/dashboard', label: 'Dashboard', anyOf: ['dashboard:view'], group: 'Overview' },
  { to: '/app/segments', label: 'Segments', anyOf: ['reports:download', 'dashboard:drilldown'], group: 'Overview' },
  { to: '/app/leads', label: 'Leads', anyOf: ['leads:read'], group: 'Daily' },
  { to: '/app/customers', label: 'Customers', anyOf: ['customers:read'], group: 'Daily' },
  { to: '/app/background-verification', label: 'Background Verification', anyOf: ['customers:read'], group: 'Daily' },
  { to: '/app/locker-enrollment', label: 'Locker Enrollment', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-applications', label: 'Locker Applications', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-tenants', label: 'Locker Tenants', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-agreements', label: 'Locker Agreements', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-renewals', label: 'Locker Renewals', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-visits', label: 'Locker Visit Log', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/locker-rent-report', label: 'Locker Rent Report', anyOf: ['lockers:enroll'], group: 'Daily' },
  { to: '/app/applications', label: 'Applications', anyOf: ['customers:read'], group: 'Daily', hideForRoles: ['cxo'] },
  // Outstanding is a back-office queue (settle failures, pending items) — hidden
  // from agents and branch staff (owner 2026-09-09); they hold customers:read for
  // enrolment but shouldn't see it.
  { to: '/app/outstanding', label: 'Outstanding', anyOf: ['customers:read'], group: 'Daily', hideForRoles: ['agent', 'branch_staff'] },
  { to: '/app/approvals', label: 'Approvals', anyOf: ['approvals:check', 'approvals:check-premature', 'approvals:check-handover'], group: 'Daily' },
  { to: '/app/allotments', label: 'Allotments', anyOf: ['allotments:execute'], group: 'Periodic' },
  { to: '/app/redemptions', label: 'Redemptions', anyOf: ['redemptions:initiate'], group: 'Periodic' },
  { to: '/app/ncd-events', label: 'NCD Events', anyOf: ['redemptions:initiate'], group: 'Periodic' },
  { to: '/app/maturity-alerts', label: 'Maturity Alerts', anyOf: ['dashboard:view'], group: 'Periodic' },
  { to: '/app/payouts', label: 'Payouts', anyOf: ['payouts:generate'], group: 'Periodic' },
  { to: '/app/escrow', label: 'Escrow', anyOf: ['escrow:reconcile'], group: 'Periodic' },
  { to: '/app/tds-threshold', label: 'TDS ₹30L Crossings', anyOf: ['approvals:check-premature'], group: 'Periodic' },
  { to: '/app/incentives', label: 'Incentives', anyOf: ['incentives:manage-eligibility'], group: 'Periodic' },
  { to: '/app/agents', label: 'Agents', anyOf: ['agents:manage'], group: 'Periodic' },
  { to: '/app/my-earnings', label: 'My Earnings', anyOf: ['earnings:read-own'], group: 'Periodic' },
  { to: '/app/reports', label: 'Reports', anyOf: ['reports:download'], group: 'Periodic', hideForRoles: ['cxo'] },
  { to: '/app/masters', label: 'Masters', anyOf: ['products:manage'], group: 'Admin' },
  { to: '/app/system', label: 'System', anyOf: ['audit:read', 'notifications:admin'], group: 'Admin' },
  { to: '/app/settings', label: 'Settings', anyOf: ['settings:manage', 'settings:workflow-config'], group: 'Admin' },
  { to: '/app/users', label: 'Users', anyOf: ['users:manage'], group: 'Admin' },
];

export const NAV_GROUPS = ['Overview', 'Daily', 'Periodic', 'Admin'];
